"use client";

import { useEffect } from "react";
import Link from "next/link";
import { useAuth } from "../lib/auth";
import { emitSignal } from "../lib/signal";

export default function Error({ error, reset }) {
  // Session 46 tune-up: log the real error to console with its full
  // stack + fire a Radio signal before rendering the Oops UI. Next.js
  // passes the thrown error as the `error` prop here. Previous version
  // rendered silently which made tracking down runtime bugs (like the
  // isApp ReferenceError) harder than it needed to be.
  useEffect(() => {
    if (!error) return;
    try {
      console.error("[error-boundary]", error);
      emitSignal("error_boundary_hit", "error", {
        message: error?.message || String(error),
        name: error?.name || null,
        stack: (error?.stack || "").split("\n").slice(0, 10).join(" | "),
        digest: error?.digest || null,
        path: typeof window !== "undefined" ? window.location.pathname : null,
      });
    } catch {}
  }, [error]);

  // If a lapsed user hits this boundary (likely caused by a 402 from a
  // paid endpoint bubbling up), show the upsell flow instead of "Oops."
  // Graceful fallback — never make them feel the app is broken.
  let isLapsed = false;
  try { isLapsed = useAuth()?.isLapsed || false; } catch {}

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "var(--font-primary)",
        background: "var(--color-bg)",
        color: "var(--color-text-primary)",
        padding: "var(--space-6)",
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: "var(--font-size-4xl)", fontWeight: "var(--font-weight-black)", marginBottom: "var(--space-2)" }}>
        {isLapsed ? "On pause" : "Oops"}
      </div>
      <div style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)", marginBottom: "var(--space-6)", maxWidth: 320 }}>
        {isLapsed
          ? "Everything\u2019s saved. The AI side is paused \u2014 $9 flips it back on."
          : "Something went wrong. It\u2019s not you. Give it another shot or head back."}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)", width: "100%", maxWidth: 280 }}>
        {isLapsed ? (
          <Link
            href="/settings/billing"
            style={{
              display: "block",
              width: "100%",
              textAlign: "center",
              padding: "var(--space-3)",
              background: "var(--color-text)",
              color: "var(--color-bg)",
              borderRadius: "var(--radius-md)",
              fontSize: "var(--font-size-sm)",
              fontWeight: "var(--font-weight-semibold)",
              fontFamily: "var(--font-primary)",
              textDecoration: "none",
              border: "none",
            }}
          >
            Upgrade
          </Link>
        ) : (
          <button
            onClick={() => reset()}
            style={{
              display: "block",
              width: "100%",
              padding: "var(--space-3)",
              background: "var(--color-accent)",
              color: "var(--color-text-inverse)",
              borderRadius: "var(--radius-md)",
              fontSize: "var(--font-size-sm)",
              fontWeight: "var(--font-weight-semibold)",
              fontFamily: "var(--font-primary)",
              border: "none",
              cursor: "pointer",
            }}
          >
            Try again
          </button>
        )}
        <Link
          href="/"
          style={{
            display: "block",
            width: "100%",
            textAlign: "center",
            padding: "var(--space-3)",
            background: "transparent",
            border: "1px solid var(--color-border)",
            color: "var(--color-text)",
            borderRadius: "var(--radius-md)",
            fontSize: "var(--font-size-sm)",
            fontWeight: "var(--font-weight-medium)",
            fontFamily: "var(--font-primary)",
            textDecoration: "none",
          }}
        >
          Back to F{"\u00FC"}lkit
        </Link>
      </div>
    </div>
  );
}
